import type {
  AccountMonth,
  DimensionFilters,
  Id,
  MonthPeriod,
  SearchableTransaction,
  SeriesResult,
  YearMonth,
} from '../data/types';
import { matchesDimensionFilters } from './flow';

export interface CategorizedBalanceQuery {
  breakdown: 'none' | 'account';
  accountIds?: ReadonlySet<Id>;
  categoryIds: ReadonlySet<Id>;
  includeUncategorized?: boolean;
  visiblePeriod: MonthPeriod;
}

export function queryCategorizedBalance(
  accountMonths: readonly AccountMonth[],
  transactions: readonly SearchableTransaction[],
  months: readonly YearMonth[],
  query: CategorizedBalanceQuery,
): SeriesResult {
  if (query.visiblePeriod.from > query.visiblePeriod.to) {
    throw new RangeError('Il periodo visibile è invertito.');
  }

  const visibleMonths = months.filter(
    (month) => month >= query.visiblePeriod.from && month <= query.visiblePeriod.to,
  );
  if (visibleMonths.length === 0) {
    return { buckets: [], series: [], total: 0, unit: 'cents' };
  }

  const firstMonth = visibleMonths[0]!;
  const monthIndex = new Map(visibleMonths.map((month, index) => [month, index]));
  const openingByAccount = new Map<Id, number>();
  for (const row of accountMonths) {
    if (row.month !== firstMonth) continue;
    if (query.accountIds !== undefined && !query.accountIds.has(row.account_id)) continue;
    openingByAccount.set(
      row.account_id,
      (openingByAccount.get(row.account_id) ?? 0) + row.closing_balance_cents - row.delta_cents,
    );
  }

  const filters: DimensionFilters = {
    ...(query.accountIds !== undefined ? { accountIds: query.accountIds } : {}),
    categoryIds: query.categoryIds,
    ...(query.includeUncategorized !== undefined
      ? { includeUncategorized: query.includeUncategorized }
      : {}),
  };
  const movementsByAccount = new Map<Id, number[]>();
  for (const transaction of transactions) {
    if (transaction.flow === 'transfer') continue;
    const index = monthIndex.get(transaction.month);
    if (index === undefined || !matchesDimensionFilters(transaction, filters)) continue;
    const values =
      movementsByAccount.get(transaction.account_id) ?? Array<number>(visibleMonths.length).fill(0);
    values[index] = (values[index] ?? 0) + transaction.amount_cents;
    movementsByAccount.set(transaction.account_id, values);
  }

  const accountIds = Array.from(
    new Set([...openingByAccount.keys(), ...movementsByAccount.keys()]),
  ).sort((left, right) => left.localeCompare(right));

  if (query.breakdown === 'none') {
    let balance = Array.from(openingByAccount.values()).reduce((sum, value) => sum + value, 0);
    const values = visibleMonths.map((_, index) => {
      for (const movements of movementsByAccount.values()) balance += movements[index] ?? 0;
      return balance;
    });
    return {
      buckets: visibleMonths,
      series: [{ id: 'total', label: 'total', values }],
      total: values.at(-1) ?? 0,
      unit: 'cents',
    };
  }

  const series = accountIds.map((id) => {
    let balance = openingByAccount.get(id) ?? 0;
    const movements = movementsByAccount.get(id) ?? [];
    return {
      id,
      label: id,
      values: visibleMonths.map((_, index) => (balance += movements[index] ?? 0)),
    };
  });
  return {
    buckets: visibleMonths,
    series,
    total: series.reduce((sum, item) => sum + (item.values.at(-1) ?? 0), 0),
    unit: 'cents',
  };
}
